import React, { FocusEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NavLink } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { usersApi } from "../../api";
import {
  APP_ROUTES,
  IResponseError,
  VALID_EMAIL_REGEX,
  VALIDATION_MESSAGES,
} from "../../data-access";
import { ExtensionInfo, FormInput, FormButton, BrandLogo } from "../../ui";
import { notify, handleFailedRequest, sanitizeEmail } from "../../util";
import * as yup from "yup";
import "./login.scss";

interface ILoginFormData {
  email: string;
}

const validationScheme = yup
  .object({
    email: yup
      .string()
      .required()
      .matches(VALID_EMAIL_REGEX, VALIDATION_MESSAGES.INVALID_EMAIL),
  })
  .required();

export const Login = (): JSX.Element => {
  const [isProcessingRequest, setIsProcessingRequest] =
    useState<boolean>(false);

  const form = useForm<ILoginFormData>({
    mode: "onBlur",
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(validationScheme),
  });
  const {
    control,
    resetField,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields },
  } = form;

  const handleFormSubmit = (formFields: ILoginFormData): void => {
    setIsProcessingRequest(true);

    usersApi
      .authenticate(formFields.email)
      .then(() => {
        notify("success", "Please check your email to confirm the login");
        resetField("email");
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsProcessingRequest(false));
  };

  const handleEmailInput = (e: FocusEvent<HTMLInputElement>): void => {
    const sanitizedEmail = sanitizeEmail(e?.target?.value);
    setValue("email", sanitizedEmail, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const isFormValid =
    Object.keys(dirtyFields).length > 0 && !errors?.email?.message;

  return (
    <div className="login">
      <div className="login__column login__column--left">
        <BrandLogo />
        <div className="login__content">
          <h1 className="login__title">Sign in</h1>
          <p className="login__description">
            Connect using the email address. You will receive a link in your
            inbox.
          </p>
          <form
            className="login__form"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <Controller
              control={control}
              name="email"
              render={({ field: { name, onBlur, value, ref } }) => (
                <FormInput
                  ref={ref}
                  placeholder="Enter your email address"
                  error={errors?.email?.message}
                  name={name}
                  value={value}
                  disabled={isProcessingRequest}
                  onBlur={onBlur}
                  onChange={handleEmailInput}
                />
              )}
            />
            <FormButton
              className="login__button"
              type="primary"
              htmlType="submit"
              loading={isProcessingRequest}
              disabled={!isFormValid || isProcessingRequest}
            >
              Send magic link
            </FormButton>
            <span className="login__link">
              Don't have an account?
              <NavLink to={APP_ROUTES.registration}>Register here</NavLink>
            </span>
          </form>
        </div>
      </div>
      <div className="login__column--right login__column">
        <ExtensionInfo />
      </div>
    </div>
  );
};
